import React from "react";
import DataTableActions from "components/common/datatable/DataTableActions";
import i18next from "i18next";
import {
  EADataGridCell,
  cellWithTooltip
} from "components/common/datatable/EADataGrid";
import MainHeaderCell from "components/common/datatable/MainHeaderCell";
import StatusChip from "components/common/StatusChip";
import { formatDate } from "services/common/Utils";
import { isSealedDossier } from "services/common/Constants";
import { PrivilegeEnum } from '../../../../services/common/RolesUtils';

const dossierModelTableActions = (
  dossierModel,
  locked,
  active,
  associatedToDossiers,
  openReadDialog,
  openEditDialog,
  openDeleteDialog,
  changeNature,
  changeActive,
  code,
  hasAnyOfPrivileges
) => {
  return [
    {
      label: i18next.t('read'),
      icon: 'open-eye',
      action: () => openReadDialog(dossierModel[0]),
      isDisabled: !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE),
    },
    {
      label: i18next.t('modify'),
      icon: 'edit_grey',
      action: () => openEditDialog(dossierModel[0]),
      isDisabled:
        isSealedDossier(code) ||
        !hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_TYPE
        ),
    },
    {
      label:
        locked === true
          ? i18next.t('demandes.dossierModel.lock.actions.unlock')
          : i18next.t('demandes.dossierModel.lock.actions.lock'),
      icon: locked === true ? 'demandes/unlock_gray' : 'demandes/lock_gray',
      action: () => changeNature(dossierModel, locked === false),
      isDisabled: isSealedDossier(code)||
        !hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_TYPE
        ),
    },
    {
      label:
        active === true
          ? i18next.t('demandes.dossierModel.status.actions.disable')
          : i18next.t('demandes.dossierModel.status.actions.enable'),
      icon: active === true ? 'pause_circle_filled' : 'play_circle_filled',
      action: () => changeActive(dossierModel, active === false),
      isDisabled: isSealedDossier(code)||
        !hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_TYPE_STATUS
        ),
    },
    {
      label: i18next.t('delete'),
      icon: 'delete_grey',
      action: () => {
        if (!associatedToDossiers) {
          openDeleteDialog(dossierModel);
        }
      },
      isDisabled: associatedToDossiers || isSealedDossier(code)||
        !hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_DELETE_DOSSIER_TYPE
        ),
    },
  ];
};

export const dossierModelListColumns = (openReadDialog, openEditDialog, openDeleteDialog, changeNature, changeActive, hasAnyOfPrivileges) => {
  return [
    {
      field: "libelle",
      headerName: i18next.t("demandes.dossierModel.libelle"),
      flex: 0.1,
      renderHeader: () => (
        <MainHeaderCell name={i18next.t("demandes.dossierModel.libelle")} />
      ),
      renderCell: (params) => cellWithTooltip(params.value),
    },
    {
      field: "code",
      headerName: i18next.t("demandes.dossierModel.code"),
      flex: 0.1,
      renderCell: (params) => cellWithTooltip(params.value),
    },
    {
      field: "nbDocuments",
      headerName: i18next.t("demandes.dossierModel.nbDocuments"),
      flex: 0.1,
      renderCell: (params) => cellWithTooltip(params.value),
    },
    {
      field: "creationDate",
      headerName: i18next.t("demandes.dossierModel.creationDate"),
      flex: 0.1,
      type: "date",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <EADataGridCell>
          {formatDate(params.value)}
        </EADataGridCell>
      )
    },
    {
      field: "creatorName",
      headerName: i18next.t("demandes.dossierModel.creatorName"),
      flex: 0.1,
      renderCell: (params) => cellWithTooltip(params.value),
    },
    {
      field: "updatedDate",
      headerName: i18next.t("demandes.dossierModel.updatedDate"),
      flex: 0.1,
      type: "date",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <EADataGridCell>
          {formatDate(params.value)}
        </EADataGridCell>
      )
    },
    {
      field: "updateActorName",
      headerName: i18next.t("demandes.dossierModel.updateActorName"),
      flex: 0.1,
      renderCell: (params) => cellWithTooltip(params.value),
    },
    {
      field: "locked",
      headerName: i18next.t("demandes.dossierModel.lock.title"),
      flex: 0.1,
      type: "singleSelect",
      valueOptions: [
        { value: true, label: i18next.t("demandes.dossierModel.lock.options.locked") },
        { value: false, label: i18next.t("demandes.dossierModel.lock.options.unlocked") },
      ],
      valueFormatter: (params) =>
        params.value === true
          ?
          i18next.t("demandes.dossierModel.lock.options.locked")
          :
          i18next.t("demandes.dossierModel.lock.options.unlocked"),
      renderCell: (params) => (
        <EADataGridCell
        >
          {i18next.t(`demandes.dossierModel.lock.options.${params.value ? "locked" : "unlocked"}`)}
        </EADataGridCell>
      )
    },
    {
      field: "active",
      headerName: i18next.t("demandes.dossierModel.status.title"),
      flex: 0.1,
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: [
        { value: true, label: i18next.t("demandes.dossierModel.status.options.active") },
        { value: false, label: i18next.t("demandes.dossierModel.status.options.inactive") },
      ],
      valueFormatter: (params) =>
        params.value === true
          ?
          i18next.t("demandes.dossierModel.status.options.active")
          :
          i18next.t("demandes.dossierModel.status.options.inactive"),
      renderCell: (params) => {
        return (
          <EADataGridCell
            tooltip={false}
          >
            <StatusChip status={params.value ? "active" : "inactive"} label={"demandes.dossierModel.status.options"} />
          </EADataGridCell>
        );
      }
    },
    {
      field: "action",
      headerName: i18next.t("actions"),
      flex: 0.15,
      sortable: false,
      filterable: false,
      disableExport: true,
      hideSortIcons: true,
      disableReorder: true,
      disableColumnMenu: true,
      disableMultipleSelection: true,
      disableSelectionOnClick: true,
      isRowSelectable: false,
      hideFooterSelectedRowCount: true,
      align: "center",
      renderCell: (params) => {
        return (
          <div style={{ textAlign: "center" }}>
            <DataTableActions
              actions={dossierModelTableActions(
                [params.row.id],
                params.row.locked,
                params.row.active,
                params.row.associatedToDossiers,
                openReadDialog,
                openEditDialog,
                openDeleteDialog,
                changeNature,
                changeActive,
                params.row.code,
                hasAnyOfPrivileges
              )}
            />
          </div>
        );
      },
    },
  ];
};
